import _ from "lodash";
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";

// Hooks
import useTextToSpeech from "Hooks/useTextToSpeech";

// components
import AIChat from "UIComponents/AIChat";
import I18nHOC from "UIComponents/I18nHOC";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import Loading from "UIComponents/Loading";
import AITutorChat from "./components/AITutorChat";
import AIChatFooter from "./components/AIChatFooter";
import AIChatBody from "./components/AIChatBody/AIChatBody";
import TextWithEllipsis from "UIComponents/TextWithEllipsis";

// design system
import {
  Tabs,
  Button,
  Dialog,
  IconButton,
  EmptyState,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  RefreshArrowOutlined,
  ChevronLeftOutlined,
  CommentSquareLeftClassroomOutlined,
  CaretOpenedFilled,
  CaretClosedFilled,
} from "@toddle-edu/ds-icons";
import { NoMessagesIllustration } from "@toddle-edu/ds-theme";

// modules
import {
  getUserType,
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
} from "modules/CommonSelectors";
import { getAssessmentDetailsForAIEnhancer } from "Assessments/modules/AssessmentQueryEnhancers";
import { getAITutorConversationsEnhancer } from "./modules/AITutorChatEnhancers";
import { updateAIDataInRedux } from "Assessments/modules/AssessmentModule";
import {
  createOpeningMessage,
  generateProficiencyLevelConversation,
  createAITutorMessage,
  resetAITutorConversation,
  createAITutorConversation,
  cancelRequest,
  cancelTTSRequest,
} from "./modules/AITutorChatModule";
import {
  getAITutorConversationFromCache,
  getAITutorConversation,
} from "./modules/AITutorChatGraphqlHelpers";
import { getStudentAssignmentDetailsFromCache } from "ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getAITutorLibraryAssessmentDetailsQuery } from "./modules/AITutorChatQueries";
import {
  deleteAssessmentToolResponse,
  updateEvaluationData,
  updateAIData,
} from "ClassRoom/modules/ClassRoomModule";
import { updateAIConversation } from "modules/Services";
import {
  onSendQuery,
  updateAIMessage,
} from "AppComponents/AITutorChatWrapper/modules/AITutorChatModule";

// third party libs
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";
import classNames from "classnames";

// utils
import {
  getConversationIdByEntitySubType,
  getEntitySubType,
  getTabOptions,
  isPlayMode,
  isUserEngagedWithAI,
  extractStateAndContent,
} from "./modules/Utils";
import {
  getToolResponseUserFilter,
  getAIAnalysisItem,
} from "ClassRoom/modules/utils";
import { htmlToText, scrollAndHighlightBackground, isIdEqual } from "Utils";
import { getEmojisAndTextFromString } from "Utils/StringHelpers";
import {
  getPromptLabelsObj,
  getCanGenerateAITutorPreview,
  getAssessmentFieldValue,
} from "Utils/AIUtils";
import { getIsAITutorModalitiesEnabled } from "Utils/ACLStoreUtils";

// constants
import {
  AI_TUTOR_TTS_MODEL,
  DIALOGS_INFO,
  LOADER_STATES,
} from "./modules/Constants";
import { ATTACHMENT_OMIT_ATTRIBUTES } from "store/static";

// scss
import classes from "./AITutorChatWrapper.scss";

const INITIAL_STATE = {
  DIALOG_ACTION: {
    action: "",
    params: {},
  },
};

const iconButtonProps = {
  dsVersion: "2.0",
  variant: "neutral",
  type: "plain",
  size: "small",
};

const styles = {
  emptyStateSubtitleStyle: {
    fontSize: "1.6rem",
  },
  tooltipOverlayInnerStyle: {
    maxWidth: "496px",
  },
  rotateBy90: { transform: "rotate(90deg)" },
  aiChatInnerContainerStyle: {
    backgroundColor: "var(--surface-primary-enabled)",
    border: "1px solid var(--border-secondary)",
    borderRadius: "8px",
    overflow: "hidden",
    maxWidth: "802px",
  },
  collapsedInnerContainer: {
    minWidth: "44px",
  },
  containerWithModality: {
    flex: 0,
  },
  animationStyles: {
    transition: "flex-basis 240ms ease, width 240ms ease, max-width 240ms ease",
    willChange: "flex-basis, width, max-width",
  },
};

const BREAKPOINT_SCREEN_WIDTH = 1200;

const AITutorChatWrapper = props => {
  const {
    // HOC props
    t,
    assessmentDetails,
    conversations,
    isInitialState = true, // it means 0 messages
    isStaff,
    isEditMode,
    messages,
    isLibrary,
    isStudentPreview,
    isPreviewChat,
    studentAssignmentData,
    studentAssignmentVariables,
    selectedTab,
    currentUpdatedField,
    assessmentRequiredFieldValues,
    metadataRequiredFieldValues,
    aiTutorPreviewChatSelectedTabs,
    aiAnalysisCriteria,
    isData,
    isLoading,
    analysisToolEvaluation,
    conversationMetadata,
    isStudent,
    highlightMessageId,
    onModalityStateChange = _.noop,

    // actual props
    mode,
    selectedPrompt,
    emptyStateProps,
    course,
    entityId,
    gradeId,
    shouldGeneratePreviewChat,
    aiConversationId,
    showEmptyState,
    conversationEntityType,
    leId,
    entityType,
    entitySubType,
    source,
    onClickEndSession,
    showTabs,
    onContentCreationComplete,
    showHeader,
    assessmentMode,
    shouldResetChat,
    headerRight = null,
    courseId,
    learningCourseId,
    showCompactUI,
    onClose,
    handleViewInstructions,
    onAIGeneratingChange,
    footerRef,

    // action creators
    createAITutorConversation,
    createOpeningMessage,
    resetAITutorConversation,
    updateAIDataInRedux,
    updateAIData,
    generateProficiencyLevelConversation,
    createAITutorMessage,
    deleteAssessmentToolResponse,
    updateAIConversation,
    updateEvaluationData,
    updateAIMessage,
    onSendQuery,
  } = props;

  const allFields = _.get(assessmentDetails, "allFields", []);
  const studentCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "studentCommunicationMode",
  });
  const isSpeakingOnly = studentCommunicationMode === "speaking_only";

  const isCriteriaUpdated = _.some(aiAnalysisCriteria, {
    id: currentUpdatedField,
  });

  const testId = `assessment-aiTutorChatWrapper${
    source ? `-${_.lowerCase(source)}` : ""
  }`;

  const tabOptions = getTabOptions({
    t,
    criteria: aiAnalysisCriteria,
    selectedTab,
    source,
    isSpeakingOnly,
  });

  const hasFlaggedMessageDisclaimer = _.some(messages, {
    node: { authorType: "flaggedMessage" },
  });

  const showFooter =
    mode === "edit" &&
    !shouldGeneratePreviewChat &&
    !hasFlaggedMessageDisclaimer;

  // ------------------ states -----------------------
  const [loaderType, setLoaderType] = useState({});
  const [currentDialogAction, setCurrentDialogAction] = useState(
    INITIAL_STATE.DIALOG_ACTION
  );
  const [currentAIState, setCurrentAIState] = useState({}); // WRITING, STREAMING etc.
  const [isAnalysing, setIsAnalysing] = useState({});
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [hasSelectedModality, setHasSelectedModality] = useState(false);
  const [showChatContainerFullWidth, setShowChatContainerFullWidth] = useState(
    true
  );
  const [showCollapsedModality, setShowCollapsedModality] = useState(false);
  const [isChatContainerAnimating, setIsChatContainerAnimating] = useState(
    false
  );
  const [isScreenSmall, setIsScreenSmall] = useState(
    typeof window !== "undefined"
      ? window.innerWidth < BREAKPOINT_SCREEN_WIDTH
      : false
  );

  const onAudioURLReceived = async ({ itemId, generatedAudioURL }) => {
    if (!_.includes(itemId, "-") && !_.isEmpty(generatedAudioURL)) {
      try {
        // Read the conversation from cache to get messages
        const conversationData = getAITutorConversationFromCache({
          id: aiConversationId,
        });

        let cachedMessages = _.get(conversationData, "messages.edges", []);
        if (_.isEmpty(cachedMessages)) {
          cachedMessages = messages;
        }

        const message = _.find(cachedMessages, ({ node }) =>
          isIdEqual(node?.id, itemId)
        );

        if (!_.isEmpty(message)) {
          const input = {
            id: message?.node?.id,
            metadata: {
              ..._.get(message, "node.metadata", {}),
              audioUrl: generatedAudioURL,
            },
          };

          updateAIMessage({ input });
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const {
    currentlyAudioPlayingForItemId,
    setCurrentlyAudioPlayingForItemId,
    isAudioGenerating,
    handleSpeakClick,
  } = useTextToSpeech({
    onAudioURLReceived,
    model: getIsAITutorModalitiesEnabled() ? AI_TUTOR_TTS_MODEL : undefined,
  });

  const onSpeakMessageClick = async node => {
    const { id, metadata, content } = node || {};
    const { content: extractedContent, output: outputTagContent } =
      extractStateAndContent(content) || {};
    const cleanedContent = extractedContent || outputTagContent;

    await handleSpeakClick({
      itemId: id,
      entityType: "AI_TUTOR_MESSAGE",
      content: cleanedContent,
      audioURL: _.get(metadata, "audioUrl"),
    });
  };

  // ------------------ refs -----------------------

  const isContentGenerating = useRef(false);
  const isUpdatingMetadata = useRef(false);
  const flaggedMessagesCount = useRef(0);
  const aiChatRef = useRef(null);
  const ttsAudioRegistryRef = useRef(new Map());
  const isMountedRef = useRef(true);

  // ------------------ side effects -----------------------
  const isAIGenerating = !_.isEmpty(
    selectedTab
      ? currentAIState?.[selectedTab]
      : _.first(_.values(currentAIState))
  );

  // ====== effects ========

  // cleanup on unmount: stop playing audio and clear ttsAudioRegistry
  useEffect(() => {
    const ttsAudioRegistry = ttsAudioRegistryRef.current;
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      if (ttsAudioRegistry && getIsAITutorModalitiesEnabled()) {
        // Stop all audio playback and clean up resources
        ttsAudioRegistry.forEach(entry => {
          const { audioEl, mediaSource } = entry || {};

          if (audioEl) {
            audioEl.pause();
            audioEl.src = "";
          }

          if (mediaSource && mediaSource.readyState === "open") {
            try {
              mediaSource.endOfStream();
            } catch {
              // Ignore errors during cleanup
            }
          }
        });

        ttsAudioRegistry.clear();
      }

      setCurrentlyAudioPlayingForItemId(null);
    };
  }, []);

  // Pause audio if the currently playing message is deleted
  useEffect(() => {
    if (!currentlyAudioPlayingForItemId || !getIsAITutorModalitiesEnabled()) {
      return;
    }

    const isPlayingIdPresent = _.some(
      messages,
      ({ node }) => node?.id === currentlyAudioPlayingForItemId
    );

    if (!isPlayingIdPresent) {
      const ttsAudioRegistry = ttsAudioRegistryRef.current;
      const entry = ttsAudioRegistry.get(currentlyAudioPlayingForItemId);

      if (entry && entry.audioEl) {
        entry.audioEl.pause();
        entry.audioEl.src = "";
      }
      ttsAudioRegistry.delete(currentlyAudioPlayingForItemId);
      setCurrentlyAudioPlayingForItemId(null);
    }
  }, [messages, currentlyAudioPlayingForItemId]);

  // if user had clicked on text to speech icon while the text is being generated then the id in cache will be temporary id (conversationId-index)
  // once the cache is updated with the actual id, we need to update messageAudioPlayingForId with the actual id
  useEffect(() => {
    if (
      !_.isEmpty(currentlyAudioPlayingForItemId) &&
      _.includes(currentlyAudioPlayingForItemId, "-") &&
      !_.some(messages, ({ node }) => _.includes(node?.id, "-"))
    ) {
      const messageIndex = _.last(_.split(currentlyAudioPlayingForItemId, "-"));
      const message = _.get(messages, messageIndex, {});
      setCurrentlyAudioPlayingForItemId(_.get(message, "node.id"));
    }
  }, [
    messages,
    currentlyAudioPlayingForItemId,
    setCurrentlyAudioPlayingForItemId,
  ]);

  // Handle window resize to track screen size
  useEffect(() => {
    const handleResize = _.debounce(() => {
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
    }, 300);

    if (typeof window !== "undefined") {
      window.addEventListener("resize", handleResize);
      setIsScreenSmall(window.innerWidth < BREAKPOINT_SCREEN_WIDTH);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, []);

  useEffect(() => {
    if (!hasSelectedModality) {
      setShowChatContainerFullWidth(true);
    }
  }, [hasSelectedModality]);

  useEffect(() => {
    onAIGeneratingChange?.(isAIGenerating);
  }, [isAIGenerating, onAIGeneratingChange]);

  useEffect(() => {
    onModalityStateChange?.(hasSelectedModality);
  }, [hasSelectedModality, onModalityStateChange]);

  // set the first available tab as selected
  const promptAction = _.get(selectedPrompt, "children.0.action");
  useEffect(() => {
    if (showTabs) {
      const firstTabOption = _.first(tabOptions)?.value;

      const updatedTabs = {
        ...aiTutorPreviewChatSelectedTabs,
        [source]: firstTabOption,
      };

      updateAIDataInRedux({ aiTutorPreviewChatSelectedTabs: updatedTabs });
    }
  }, [showTabs, source, promptAction]);

  useEffect(() => {
    if (showTabs && isSpeakingOnly) {
      const playMode = _.find(tabOptions, { isPlayMode: true })?.value;

      const updatedTabs = {
        ...aiTutorPreviewChatSelectedTabs,
        [source]: playMode,
      };

      updateAIDataInRedux({ aiTutorPreviewChatSelectedTabs: updatedTabs });
    }
  }, [showTabs, isSpeakingOnly]);

  // generate chatbot on initial load based on if shouldGeneratePreviewChat is true or not
  useEffect(() => {
    (async () => {
      if (isContentGenerating.current) return;

      if (shouldGeneratePreviewChat) {
        const { DEFAULT, CREATING_CHATBOT, UPDATING_CHATBOT } = LOADER_STATES;

        // always show plain loader to student
        let loaderType = DEFAULT;
        if (isStaff && !isStudentPreview) {
          if (isUserEngagedWithAI(messages)) {
            loaderType = UPDATING_CHATBOT;
          } else {
            loaderType = CREATING_CHATBOT;
          }
        }

        // if AI analysis criteria updated or not, if updated then re-generate data for that tab
        const tabToUpdate = isCriteriaUpdated
          ? currentUpdatedField
          : selectedTab;

        updateLoaderType(tabToUpdate, loaderType);

        if (!isLoading) {
          isContentGenerating.current = true;

          await generateConversation(tabToUpdate);

          updateLoaderType(tabToUpdate, "");

          updateAIDataInRedux({
            shouldGeneratePreviewChat: false,
            updatedFields: [],
          });

          isContentGenerating.current = false;

          onContentCreationComplete?.();
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldGeneratePreviewChat, isLoading]);

  // update metadata when assessmentRequiredFieldValues are updated
  // it helps to know if tutor is updated or not
  useEffect(() => {
    if (
      isUpdatingMetadata.current ||
      isLoading ||
      loaderType?.[selectedTab] ||
      currentAIState?.[selectedTab] ||
      !(isPreviewChat || isStudentPreview || isStudent)
    ) {
      return;
    }

    if (
      aiConversationId &&
      !_.isEmpty(assessmentRequiredFieldValues) &&
      !_.isEqual(metadataRequiredFieldValues, assessmentRequiredFieldValues)
    ) {
      const updateAssessmentRequiredFields = async () => {
        isUpdatingMetadata.current = true;

        await updateAIConversation({
          input: {
            id: aiConversationId,
            metadata: {
              ...(conversationMetadata || {}),
              assessmentRequiredFieldValues,
            },
          },
        });

        isUpdatingMetadata.current = false;
      };

      updateAssessmentRequiredFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentAIState,
    loaderType,
    isLoading,
    conversationMetadata,
    aiConversationId,
    metadataRequiredFieldValues,
    assessmentRequiredFieldValues,
  ]);

  // highlight the message with id = highlightMessageId in the chat
  useEffect(() => {
    if (isLoading) return;

    if (highlightMessageId) {
      const element = scrollAndHighlightBackground({
        id: highlightMessageId,
        className: classes.orangeBackground,
      });

      if (element) updateEvaluationData({ highlightMessageId: null });
    }
  }, [highlightMessageId, isLoading]);

  // ------------------ functions -----------------------

  const CHAT_TOGGLE_ANIMATION_DURATION_MS = !showChatContainerFullWidth
    ? 240
    : 0;

  const updateLoaderType = (tabValue, loaderType) => {
    setLoaderType(prevState => {
      return { ...prevState, [tabValue]: loaderType };
    });
  };

  const updateCurrentAIState = (tabValue, state) => {
    setCurrentAIState(prevState => {
      return { ...prevState, [tabValue]: state };
    });
  };

  const updateIsAnalysing = (tabValue, isAnalysing) => {
    setIsAnalysing(prevState => {
      return { ...prevState, [tabValue]: isAnalysing };
    });
  };

  const handleModalityStateChange = useCallback(hasModality => {
    setHasSelectedModality(hasModality);
    onModalityStateChange(hasModality);
  }, []);

  const onToggleChatContainer = useCallback(() => {
    if (isChatContainerAnimating) return;

    setIsChatContainerAnimating(true);

    setShowChatContainerFullWidth(prevState => {
      const nextState = !prevState;
      if (isScreenSmall) {
        setShowCollapsedModality(nextState);
      } else {
        setShowCollapsedModality(false);
      }

      return nextState;
    });

    setTimeout(() => {
      setIsChatContainerAnimating(false);
    }, CHAT_TOGGLE_ANIMATION_DURATION_MS);
  }, [isChatContainerAnimating]);

  const onToggleModalityContainer = useCallback(() => {
    if (isChatContainerAnimating) return;

    setIsChatContainerAnimating(true);
    setShowCollapsedModality(prevState => {
      const nextState = !prevState;
      setShowChatContainerFullWidth(nextState);

      return nextState;
    });

    setTimeout(() => {
      setIsChatContainerAnimating(false);
    }, CHAT_TOGGLE_ANIMATION_DURATION_MS);
  }, [isChatContainerAnimating]);

  const canGenerateChat = selectedTab => {
    // when teacher is checking the student preview, they can always generate chat without any restrictions
    if (!selectedTab || isStudentPreview) return true;

    // in library only "play_mode" need to be generated, other tabs data is fetched from GOD org
    if (isLibrary) return isPlayMode(selectedTab);
    else if (isStaff) {
      const canGenerateTutorPreview = getCanGenerateAITutorPreview({
        assessmentDetails,
      });

      const currentFieldData = _.get(
        _.find(metadataRequiredFieldValues, currentUpdatedField),
        currentUpdatedField
      );

      const updatedFieldData = _.get(
        _.find(assessmentRequiredFieldValues, currentUpdatedField),
        currentUpdatedField
      );

      // allow generating data only when all required fields are filled or there is mismatch between previous and current field value
      if (
        !canGenerateTutorPreview ||
        (!_.isEmpty(currentFieldData) &&
          !_.isEmpty(updatedFieldData) &&
          _.isEqual(_.trim(currentFieldData), _.trim(updatedFieldData)))
      ) {
        return false;
      }

      if (_.isEmpty(metadataRequiredFieldValues)) {
        updateAIDataInRedux({ updatedFields: [] });
      }
    }

    return true;
  };

  const handleProficiencyLevelsGeneration = async params => {
    const { selectedTab, entityConversationId } = params || {};

    // if criteria is updated then reset only that tab
    // if whole tutor is updated (multiple fields) then reset conversation in all proficiency levels
    const ids = _.reduce(
      aiAnalysisCriteria,
      (acc, criterion) => {
        if (isCriteriaUpdated && criterion?.id !== currentUpdatedField) {
          return acc;
        }

        const id = getConversationIdByEntitySubType({
          assessmentDetails,
          entitySubType: criterion?.id,
        });

        if (id) acc.push(id);

        return acc;
      },
      []
    );

    if (!_.isEmpty(ids)) await resetAITutorConversation({ input: { ids } });

    if (!isPlayMode(selectedTab)) {
      await generateResponseForSelectedTab({
        selectedTab,
        conversationId: entityConversationId,
      });
    }
  };

  const handlePlayModeGeneration = async () => {
    const entityConversationId = getConversationIdByEntitySubType({
      assessmentDetails,
      entitySubType: "PREVIEW",
    });

    if (!entityConversationId || isCriteriaUpdated) return;

    const conversation = getAITutorConversationFromCache({
      id: entityConversationId,
    });

    const messages = _.get(conversation, "messages.edges", []);

    const isTutorUpdatedInLastMessage =
      _.get(_.first(messages), "node.authorType") === "tutorUpdated";

    let isTutorUpdated = false;

    if (_.isEmpty(messages)) {
      await generateResponseForSelectedTab({
        selectedTab: `${source}:play_mode`,
        conversationId: entityConversationId,
      });

      isTutorUpdated = true;
    } else {
      if (isUserEngagedWithAI(messages, 1)) {
        if (!isTutorUpdatedInLastMessage) {
          await createAITutorMessage({
            variables: {
              input: {
                conversationId: entityConversationId,
                authorType: "tutorUpdated",
                content: "ai:tutor_updated",
                metadata: {},
              },
            },
          });
        }
      } else {
        await resetAITutorConversation({
          input: { ids: [entityConversationId] },
        });

        await generateResponseForSelectedTab({
          selectedTab: `${source}:play_mode`,
          conversationId: entityConversationId,
        });
      }

      isTutorUpdated = true;
    }

    if (isTutorUpdated) {
      updateAIDataInRedux({
        isAITutorUpdated: isStaff && assessmentMode === "edit",
      });
    }
  };

  // the main function to generate the content
  const generateConversation = async selectedTab => {
    if (!canGenerateChat(selectedTab)) return;

    // make entityType from props where tabs are not present (student end)
    let _entitySubType = entitySubType;

    // calculate entityType based on selected tab
    if (showTabs) {
      _entitySubType = getEntitySubType({
        selectedPrompt,
        tabValue: selectedTab,
      });
    }

    const entityConversationId = getConversationIdByEntitySubType({
      assessmentDetails,
      studentAssignmentData,
      entitySubType: _entitySubType,
    });

    let conversation = getAITutorConversationFromCache({
      id: entityConversationId,
    });

    if (_.isEmpty(conversation) && entityConversationId) {
      conversation = await getAITutorConversation({ id: entityConversationId });
    }

    // if chat already exists then reset / re-generate
    if (
      isPreviewChat &&
      entityConversationId &&
      shouldGeneratePreviewChat &&
      isUserEngagedWithAI(conversation?.messages?.edges)
    ) {
      // CASE 2 : Make the necessary changes in "play_mode" tab ("play_mode" is updated in all the cases)
      await handlePlayModeGeneration();

      // CASE 1 : Reset all the proficiency levels
      await handleProficiencyLevelsGeneration({
        selectedTab,
        entityConversationId,
      });
    } else if (entityConversationId) {
      // if conversation exists without any content then generate content
      if (_.size(conversation?.messages?.edges) === 0) {
        await generateResponseForSelectedTab({
          selectedTab,
          conversationId: entityConversationId,
        });
      } else if (shouldResetChat) {
        // when teacher clicks on "Start a new session" in student preview mode, then we have to reset the chat and add opening message
        await resetChat();
      }
    } else {
      // if conversation does not exist then create one and generate content for the selected tab/entitySubType
      const { response: { id } = {} } = await createAITutorConversation({
        input: {
          entityType: conversationEntityType,
          entityId,
          entitySubType: _entitySubType,
        },
        studentAssignmentVariables,
      });

      await generateResponseForSelectedTab({
        selectedTab,
        conversationId: id,
      });
    }
  };

  // create an opening message or generate sample conversation between user and assistant from AI
  const generateResponseForSelectedTab = async params => {
    const { selectedTab, conversationId } = params;

    // whenever tutor changes, reset the math equation saved value
    updateAIData({ mathEquationLatex: "" });
    updateAIDataInRedux({
      isAITutorUpdated: isStaff && assessmentMode === "edit",
    });

    // for proficiency levels generate sample conversation
    if (_.some(aiAnalysisCriteria, { id: selectedTab })) {
      const setShowLoader = value => {
        if (value) {
          updateLoaderType(selectedTab, LOADER_STATES.PLAIN);
        } else {
          updateLoaderType(selectedTab, "");
        }
      };

      if (currentAIState?.[selectedTab]) return;

      await generateProficiencyLevelConversation({
        course,
        entityId,
        gradeId,
        conversationId,
        setShowLoader,
        updateCurrentAIState,
        selectedTab,
        updateIsAnalysing,
        learningCourseId,
      });
    } else {
      // for "play_mode" or where we are not showing tabs, generate an opening message
      await createOpeningMessage({
        leId,
        selectedPrompt,
        conversationId,
        assessmentRequiredFieldValues,
      });
    }

    updateLoaderType(selectedTab, "");
  };

  // on tab change set the updated value and check if we need to generate content for the updated tab or not
  const onTabChange = async tabValue => {
    updateAIDataInRedux({
      aiTutorPreviewChatSelectedTabs: {
        ...aiTutorPreviewChatSelectedTabs,
        [source]: tabValue,
      },
    });

    if (isPreviewChat && _.some(aiAnalysisCriteria, { id: tabValue })) {
      const entityConversationId = getConversationIdByEntitySubType({
        assessmentDetails,
        studentAssignmentData,
        entitySubType: tabValue,
      });

      if (entityConversationId) {
        updateLoaderType(tabValue, LOADER_STATES.DEFAULT);
      } else {
        updateLoaderType(tabValue, LOADER_STATES.CREATING_SAMPLE_CONVERSATION);

        setTimeout(() => {
          updateLoaderType(tabValue, LOADER_STATES.INTERACTING_WITH_THE_TUTOR);
        }, 3000);
      }
    } else {
      updateLoaderType(tabValue, LOADER_STATES.DEFAULT);
    }

    await generateConversation(tabValue);

    updateLoaderType(tabValue, "");
  };

  // render new UI custom Header
  const renderCustomHeader = () => {
    return (
      <div className={classes.headerContainer}>
        <div className={classes.headerRightContainer}>
          <CommentSquareLeftClassroomOutlined />
          <div className="text-label">{t("common:chat")}</div>
        </div>

        {hasSelectedModality ? (
          <IconButton
            {...iconButtonProps}
            icon={<CaretOpenedFilled style={styles.rotateBy90} />}
            onClick={onToggleChatContainer}
          />
        ) : null}
      </div>
    );
  };

  const aiChatInnerContainerStyles = showChatContainerFullWidth
    ? styles.aiChatInnerContainerStyle
    : styles.collapsedInnerContainer;

  const innerContainerStyle = useMemo(() => {
    return {
      ...aiChatInnerContainerStyles,
      ...(!showChatContainerFullWidth ? styles.animationStyles : {}),
      ...(!showChatContainerFullWidth && hasSelectedModality
        ? styles.containerWithModality
        : {}),
    };
  }, [
    aiChatInnerContainerStyles,
    showChatContainerFullWidth,
    hasSelectedModality,
  ]);

  // handle send button click
  const onSendMessage = async (params = {}) => {
    // return added attachments
    const getAddedAttachments = ({ attachments, audioAttachments } = {}) => {
      return _.map(attachments, attachment => {
        const _attachment = audioAttachments?.[attachment?.id] || attachment;
        return _.omit(_attachment, ATTACHMENT_OMIT_ATTRIBUTES);
      });
    };

    const { fields } = selectedPrompt || {};
    const context = _.reduce(
      fields,
      (acc, field) => {
        const { uid, value } = field || {};
        if (!_.isEmpty(value)) acc[uid] = htmlToText(value, {});
        return acc;
      },
      {}
    );

    const {
      content: query,
      setContent: setQuery = _.noop,
      attachments,
      setAttachmentItems = _.noop,
      audioTranscripts,
      audioAttachments,
      userMessageInput,
      skipAssistantMessage = false,
      assistantMetadata,
      sendButtonParams,
    } = params;

    const _attachments = getAddedAttachments({
      attachments,
      audioAttachments,
    });

    await onSendQuery({
      t,
      query,
      setQuery,
      entityId,
      aiConversationId: aiConversationId || params?.aiConversationId,
      course,
      entityType,
      gradeId,
      attachments: _attachments,
      setAttachmentItems,
      context: _.merge({}, context, params?.context),
      assessmentDetails,
      selectedTab,
      updateCurrentAIState,
      updateIsAnalysing,
      source,
      setIsEvaluating,
      flaggedMessagesCount,
      learningCourseId,
      setCurrentlyAudioPlayingForItemId,
      audioTranscripts: _.map(audioTranscripts, "transcript"),
      userMessageInput,
      sendButtonParams,
      skipAssistantMessage,
      assistantMetadata,
      ttsAudioRegistry: ttsAudioRegistryRef.current,
      isMountedRef,
    });
  };

  const onClickStop = useCallback(
    (conversationId, parentMessageId) => {
      cancelRequest?.();
      cancelTTSRequest?.();
      setCurrentAIState("");

      if (conversationId && parentMessageId) {
        createAITutorMessage({
          variables: {
            input: {
              conversationId,
              authorType: "interruptedResponse",
              content: "",
              parentMessageId,
            },
          },
        });
      }
    },
    [createAITutorMessage]
  );

  const contextProps = useMemo(
    () => ({
      onSendMessage,
      footerRef,
      showOlderVersionAlert: _.isEmpty(loaderType?.[selectedTab]),
      assessmentId: assessmentDetails?.id || leId || entityId,
      courseId,
      gradeId:
        gradeId ||
        _.get(course, "primaryGrade.id") ||
        _.get(course, "grades.0.id") ||
        _.get(assessmentDetails, "grades.0.id") ||
        _.get(assessmentDetails, "courseGrades.0.id") ||
        "",
    }),
    [
      loaderType,
      selectedTab,
      assessmentDetails,
      leId,
      entityId,
      courseId,
      gradeId,
      course,
    ]
  );

  const renderChatRevampUI = () => {
    const renderFullScreenAIChat = () => {
      const disclaimerText = t("ai:student_chat_disclaimer_msg");

      return (
        <>
          <AIChat.Header>{renderCustomHeader()}</AIChat.Header>

          {loaderType?.[selectedTab] === LOADER_STATES.DEFAULT ? (
            <Loading />
          ) : (
            <>
              <AIChatBody
                source={source}
                messages={messages}
                isEditMode={isEditMode}
                aiConversation={conversations}
                onClickStartNewSession={resetChat}
                assessmentDetails={assessmentDetails}
                flaggedMessagesCount={flaggedMessagesCount}
                currentAIState={currentAIState?.[selectedTab]}
                aiConversationId={aiConversationId}
                currentlyAudioPlayingForItemId={currentlyAudioPlayingForItemId}
                isAudioGenerating={isAudioGenerating}
                onSpeakMessageClick={onSpeakMessageClick}
                ttsAudioRegistry={ttsAudioRegistryRef.current}
              />
              {showFooter ? (
                <AIChatFooter
                  course={course}
                  messages={messages}
                  gradeId={gradeId}
                  aiConversationId={aiConversationId}
                  entityId={entityId}
                  selectedTab={selectedTab}
                  entityType={entityType}
                  assessmentDetails={assessmentDetails}
                  updateCurrentAIState={updateCurrentAIState}
                  updateIsAnalysing={updateIsAnalysing}
                  source={source}
                  setIsEvaluating={setIsEvaluating}
                  flaggedMessagesCount={flaggedMessagesCount}
                  learningCourseId={learningCourseId}
                  onSpeakMessageClick={onSpeakMessageClick}
                  onSendMessage={onSendMessage}
                  onClickStop={onClickStop}
                  ttsAudioRegistry={ttsAudioRegistryRef.current}
                />
              ) : null}

              {source === "STUDENT_PREVIEW" || source === "" ? (
                <div className={classes.disclaimerContainer}>
                  <TextWithEllipsis
                    text={disclaimerText}
                    lineHeight="1.6rem"
                    className="text-body-xs"
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      );
    };

    const isCurrentTabLoading = !_.isEmpty(loaderType?.[selectedTab]);

    return (
      <AIChat
        mode={isCurrentTabLoading ? "view" : mode}
        source={source}
        conversation={conversations}
        entityType="ASSESSMENT"
        entitySubType="ai_tutor_chat"
        shouldDisplayModalities={true}
        innerContainerStyle={innerContainerStyle}
        showCollapsedModality={showCollapsedModality}
        showChatContainerFullWidth={showChatContainerFullWidth}
        onModalityStateChange={handleModalityStateChange}
        onToggleModalityContainer={onToggleModalityContainer}
        contextProps={contextProps}
        currentAIState={currentAIState?.[selectedTab]}
        setCurrentAIState={state => updateCurrentAIState(selectedTab, state)}
        ref={aiChatRef}
      >
        {showChatContainerFullWidth
          ? renderFullScreenAIChat()
          : renderCollapsedAIChat()}
      </AIChat>
    );
  };

  const renderCollapsedAIChat = () => {
    return (
      <div className={classes.collapsedHeaderContainer}>
        <IconButton
          {...iconButtonProps}
          icon={<CaretClosedFilled style={styles.rotateBy90} />}
          onClick={onToggleChatContainer}
        />
        <div className={classNames("text-body", classes.labelContainer)}>
          {t("common:chat")}
        </div>
      </div>
    );
  };

  // render header with tabs (for staff) and end session button (for students)
  const renderHeader = () => {
    const assignmentTitle = getAssessmentFieldValue({
      fields: assessmentDetails?.allFields,
      uid: "title",
    });

    const { displayText: promptLabel } = getPromptLabelsObj({
      t,
      i18n: props.i18n,
      ...(selectedPrompt || {}),
    });

    const title = !_.isEmpty(promptLabel)
      ? _.get(getEmojisAndTextFromString(promptLabel), "text")
      : !_.isEmpty(_.trim(assignmentTitle))
      ? assignmentTitle
      : !_.isEmpty(assessmentDetails)
      ? t("common:untitled")
      : null;

    const isOpeningMessageOnly = _.every(
      messages,
      ({ node }) => node?.metadata?.isOpeningMessage
    );

    const selectedLoaderType = selectedTab
      ? loaderType?.[selectedTab]
      : _.first(_.values(loaderType));
    const selectedAIState = selectedTab
      ? currentAIState?.[selectedTab]
      : _.first(_.values(currentAIState));

    const disableButton =
      isInitialState || // 0 messages
      isOpeningMessageOnly ||
      _.some(messages, ({ node }) => _.includes(node?.id, "NEW_")) ||
      !_.isEmpty(selectedLoaderType) ||
      !_.isEmpty(selectedAIState);

    const onClickResetChat = () => {
      setCurrentDialogAction({ action: "CLEAR_CHAT", params: {} });
    };

    const backButton =
      showCompactUI && onClose ? (
        <IconButton
          dsVersion={"2.0"}
          icon={<ChevronLeftOutlined />}
          variant={"neutral"}
          type={"plain"}
          size={"medium"}
          onClick={onClose}
          className={classes.backButton}
          testId={`${testId}-backButton`}
        />
      ) : null;

    // when tabs are not shown then show "End session" button (this is specially for students / teacher end student preview)
    if (!showTabs) {
      return (
        <div className={classes.chatSubHeader}>
          <div className={classes.chatSubHeaderLeft}>
            {backButton}
            {title ? (
              <Tooltip
                dsVersion={"2.0"}
                tooltip={title}
                placement={"bottom"}
                showArrow={true}
                visible={_.size(title) > 25}
                overlayInnerStyle={styles.tooltipOverlayInnerStyle}
              >
                <div className={classNames(classes.chatTitle, "text-label-2")}>
                  {title}
                </div>
              </Tooltip>
            ) : null}
          </div>

          <div className={classes.chatSubHeaderRight}>
            {handleViewInstructions ? (
              <Button
                size={"medium"}
                variant={"neutral"}
                type={"outlined"}
                dsVersion={"2.0"}
                onClick={handleViewInstructions}
                className={classes.noShrink}
              >
                {t("common:view_instructions")}
              </Button>
            ) : null}

            {isEditMode ? (
              <Button
                size={"medium"}
                variant={"primary"}
                onClick={onClickEndSession}
                disabled={disableButton}
                dsVersion={"2.0"}
                className={classes.noShrink}
                testId={`${testId}-endSession`}
              >
                {t("ai:end_session")}
              </Button>
            ) : null}

            {headerRight}
          </div>
        </div>
      );
    } else {
      const hasLargeLabel = _.some(tabOptions, ({ label }) => {
        return _.size(label) > 30;
      });

      const tabType = hasLargeLabel ? "fixed" : "flexible";

      const refreshIconClass = classNames({
        [classes.visibilityHidden]: !isPlayMode(selectedTab),
      });

      const upperHeaderClass = classNames({
        [classes.upperHeader]: true,
        [classes.upperHeaderLibrary]: isLibrary,
        [classes.upperHeaderSpeakingOnly]: isStaff && isSpeakingOnly,
      });

      return (
        <header className={classes.header}>
          <div className={upperHeaderClass}>
            <div className={classes.upperHeaderLeft}>
              {title ? <div className={classes.headerText}>{title}</div> : null}
              {!isLibrary ? (
                <div className={classes.headerSubText}>
                  {isSpeakingOnly
                    ? t("ai:explore_play_mode_msg_speaking_only")
                    : t("ai:explore_play_mode_msg")}
                </div>
              ) : null}
            </div>

            <div className={classes.upperHeaderLeft}>
              <IconButton
                dsVersion={"2.0"}
                variant={"neutral"}
                type={"plain"}
                size={isLibrary ? "small" : "medium"}
                isCompact={isLibrary}
                icon={<RefreshArrowOutlined />}
                onClick={onClickResetChat}
                disabled={disableButton}
                className={refreshIconClass}
                testId={`${testId}-resetChat`}
              />
            </div>
          </div>

          {!isSpeakingOnly ? (
            <div className={classes.tabsContainer}>
              <Tabs
                dsVersion={"2.0"}
                options={tabOptions}
                value={selectedTab}
                onChange={onTabChange}
                type={tabType}
                variant={"inline"}
                testId={`${testId}-proficiencyTabs`}
              />
            </div>
          ) : null}
        </header>
      );
    }
  };

  // render header and AI chat
  const renderBody = () => {
    const { fields } = selectedPrompt || {};

    const context = _.reduce(
      fields,
      (acc, field) => {
        const { uid, value } = field || {};
        if (!_.isEmpty(value)) acc[uid] = htmlToText(value, {});
        return acc;
      },
      {}
    );

    const renderChat = () => {
      if (!isData && isLoading && _.isEmpty(loaderType?.[selectedTab])) {
        return <Loading />;
      }

      switch (loaderType?.[selectedTab]) {
        case LOADER_STATES.DEFAULT:
          return <Loading />;
        case LOADER_STATES.CREATING_SAMPLE_CONVERSATION: {
          return (
            <div className={classes.creatingSampleConversationContainer}>
              <div className={classes.loadingChatSubtitleContainer}>
                <div className={classes.dot} />
                <div className="text-body-1">
                  {t("ai:creating_sample_conversation")}
                </div>
              </div>
            </div>
          );
        }
        case LOADER_STATES.INTERACTING_WITH_THE_TUTOR: {
          return (
            <div className={classes.creatingSampleConversationContainer}>
              <div className={classes.loadingChatSubtitleContainer}>
                <div className={classes.dot} />
                <div className="text-body-1">
                  {t("ai:interacting_with_the_tutor")}
                </div>
              </div>
            </div>
          );
        }
        default: {
          return (
            <AITutorChat
              {...props}
              aiConversationId={aiConversationId}
              selectedTab={selectedTab}
              context={context}
              onClickStartNewSession={resetChat}
              currentAIState={currentAIState?.[selectedTab]}
              updateCurrentAIState={updateCurrentAIState}
              updateIsAnalysing={updateIsAnalysing}
              isAnalysing={isAnalysing}
              testId={testId}
              isEvaluating={isEvaluating}
              setIsEvaluating={setIsEvaluating}
              showCompactUI={showCompactUI}
              flaggedMessagesCount={flaggedMessagesCount}
              currentlyAudioPlayingForItemId={currentlyAudioPlayingForItemId}
              setCurrentlyAudioPlayingForItemId={
                setCurrentlyAudioPlayingForItemId
              }
              isAudioGenerating={isAudioGenerating}
              onSpeakMessageClick={onSpeakMessageClick}
            />
          );
        }
      }
    };

    return getIsAITutorModalitiesEnabled() ? (
      renderChatRevampUI()
    ) : (
      <>
        {showHeader ? renderHeader() : null}
        {renderChat()}
      </>
    );
  };

  // reset dialog state when user clicks the first button in dialog
  const onCancelDialogClick = () => {
    setCurrentDialogAction({
      action: "",
      params: {},
    });
  };

  // archive existing messages and generate new content (mainly opening message)
  const resetChat = async () => {
    updateLoaderType(`${source}:play_mode`, LOADER_STATES.FULL_SCREEN);

    const _entitySubType = getEntitySubType({
      selectedPrompt,
      tabValue: selectedTab,
      entitySubType,
    });

    const entityConversationId = getConversationIdByEntitySubType({
      assessmentDetails,
      studentAssignmentData,
      entitySubType: _entitySubType,
    });

    if (entityConversationId) {
      const toolResponsesToDelete = _.map(
        _.first(analysisToolEvaluation)?.toolResponses,
        "id"
      );

      if (!_.isEmpty(toolResponsesToDelete)) {
        await deleteAssessmentToolResponse({
          input: { ids: toolResponsesToDelete },
        });
      }

      await resetAITutorConversation({ input: { id: entityConversationId } });
      await generateResponseForSelectedTab({
        selectedTab,
        conversationId: entityConversationId,
      });
    }

    updateLoaderType(`${source}:play_mode`, "");

    // for video modality reset the video state
    const resetVideo = _.get(
      aiChatRef,
      "current.videoPlayerRef.current.resetVideo"
    );

    if (_.isFunction(resetVideo)) resetVideo();
  };

  // run the respective action based on currentDialogAction when user clicks the second button in dialog
  const onConfirmDialogClick = async () => {
    onCancelDialogClick();

    switch (currentDialogAction?.action) {
      case "CLEAR_CHAT":
        await resetChat();
    }
  };

  const renderLoader = () => {
    switch (loaderType?.[selectedTab]) {
      case LOADER_STATES.FULL_SCREEN:
        return <FullScreenLoader />;
      case LOADER_STATES.CREATING_CHATBOT: {
        return (
          <div className={classes.creatingChatbotContainer}>
            <div className={classes.loadingChatSubtitleContainer}>
              <div className={classes.dot} />
              <div className="text-body-1">
                {isLibrary
                  ? t("ai:creating_tutor_preview")
                  : t("ai:creating_tutor")}
              </div>
            </div>
          </div>
        );
      }
      case LOADER_STATES.UPDATING_CHATBOT: {
        return (
          <div className={classes.updatingChatbotContainer}>
            <div className={classes.loadingChatSubtitleContainer}>
              <div className={classes.dot} />
              <div className="text-body-1">
                {t("ai:updating_tutor_settings")}
              </div>
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  // ------------------ variables -----------------------

  const previewConversation = getAITutorConversationFromCache({
    id: getConversationIdByEntitySubType({
      assessmentDetails,
      entitySubType: "PREVIEW",
    }),
  });

  const isConversationEmpty = _.isEmpty(previewConversation?.messages?.edges);

  const _showEmptyState =
    showEmptyState || (isPreviewChat && isConversationEmpty);

  const { action = "" } = currentDialogAction || {};

  // ------------------ classes -----------------------

  const containerClass = classNames({
    [classes.container]: true,
    [classes.surfaceSecondary]: isLibrary,
  });

  return (
    <div className={containerClass}>
      {_showEmptyState &&
      (!selectedTab || _.isEmpty(loaderType?.[selectedTab])) ? (
        <EmptyState
          dsVersion={"2.0"}
          illustration={NoMessagesIllustration}
          subtitle={t("ai:empty_state_subtitle")}
          subtitleStyle={styles.emptyStateSubtitleStyle}
          {...emptyStateProps}
        />
      ) : (
        renderBody()
      )}

      {/* dialog box to render reset chat warning */}
      {!_.isEmpty(action) ? (
        <Dialog
          dsVersion={"2.0"}
          variant="delete-dialog"
          title={DIALOGS_INFO?.[action]?.title?.({ t })}
          message={DIALOGS_INFO?.[action]?.message?.({ t })}
          buttonOneText={DIALOGS_INFO?.[action]?.buttonOneText?.({ t })}
          buttonTwoText={DIALOGS_INFO?.[action]?.buttonTwoText?.({ t })}
          onButtonOneClick={onCancelDialogClick}
          onButtonTwoClick={onConfirmDialogClick}
          setIsDialogOpen={true}
          onClose={onCancelDialogClick}
          testId={`${testId}-dialog`}
        />
      ) : null}

      {renderLoader()}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { mode = "edit", source, selectedPrompt, entityId, leId } = ownProps;

  const userType = getUserType(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const academicYearId = getSelectedAcademicYearId(state);

  const isStaff = userType === "staff";
  const isStudent = userType === "student";

  const isEditMode = mode === "edit";
  const isLibrary = source === "LIBRARY";
  const isStudentPreview = source === "STUDENT_PREVIEW";
  const isPreviewChat = source === "PREVIEW_CHAT";

  const courseId =
    _.get(ownProps, "courseId") ||
    _.get(ownProps, "selectedCourse.courseId") ||
    _.get(state, "teacher.selected_class.selected_course");

  const aiData = _.get(state, "assessment.aiData", {});

  const highlightMessageId = _.get(
    state,
    "classRoom.evaluation.highlightMessageId",
    null
  );

  const {
    updatedFields = [],
    aiTutorSelectedTab = null,
    aiTutorPreviewChatSelectedTabs = null,
  } = aiData;

  const shouldGeneratePreviewChat =
    ownProps.shouldGeneratePreviewChat || aiData?.shouldGeneratePreviewChat;

  const selectedTab = aiTutorPreviewChatSelectedTabs?.[source];
  const entitySubType = getEntitySubType({
    selectedPrompt,
    tabValue: selectedTab,
    entitySubType: ownProps.entitySubType,
  });

  const _isPlayMode = isPlayMode(selectedTab);
  // "play_mode" conversation is created in the current org, rest are fetched from -14
  const portalType = isLibrary && !_isPlayMode ? "RESOURCE_LIBRARY" : "PLANNER";

  // this is for getAITutorConversationsEnhancer, in proficiency levels tab we manually write the complete conversation in cache
  // using "cache-and-network" causes cache data to override when the streaming is going on, using "cache-first" fixes this issue
  const fetchPolicy =
    isPreviewChat && !_isPlayMode ? "cache-first" : "cache-and-network";

  const courseGradeFilters = {
    academicYearId,
  };

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const studentAssignmentVariables = {
    id: entityId,
    showOnlySubmitted: false,
    userFilter: getToolResponseUserFilter(userType, false),
    entityEventsFilter: {
      orderBy: "EVENT_TIME",
      orderByDir: "ASC",
    },
  };

  const studentAssignmentData = getStudentAssignmentDetailsFromCache(
    studentAssignmentVariables
  );

  return {
    updatedFields,
    currentUpdatedField: _.first(updatedFields),
    isStaff,
    userType,
    isStudent,
    curriculumType,
    courseId,
    courseGradeFilters,
    entitySubType,
    aiTutorPreviewChatSelectedTabs,
    leId,
    selectedTab,
    shouldGeneratePreviewChat,
    isEditMode,
    collaboratorsFilter,
    aiTutorSelectedTab,
    studentAssignmentData,
    studentAssignmentVariables,
    isLibrary,
    isStudentPreview,
    isPreviewChat,
    portalType,
    fetchPolicy,
    highlightMessageId,
    academicYearId,
    isData: true,
    isLoading: false,
  };
};

const mapActionCreators = {
  createAITutorConversation,
  createOpeningMessage,
  resetAITutorConversation,
  generateProficiencyLevelConversation,
  updateAIDataInRedux,
  updateAIData,
  createAITutorMessage,
  deleteAssessmentToolResponse,
  updateAIConversation,
  updateEvaluationData,
  updateAIMessage,
  onSendQuery,
};

export default compose(
  I18nHOC({ resource: ["ai", "common"] }),
  connect(mapStateToProps, mapActionCreators),
  getAssessmentDetailsForAIEnhancer,
  graphql(getAITutorLibraryAssessmentDetailsQuery, {
    name: "getAITutorLibraryAssessmentDetails",
    skip: ({ selectedPrompt }) =>
      _.isEmpty(_.get(selectedPrompt, "children.0.templateAssessmentIds")),
    options: ({ selectedPrompt }) => {
      const assessmentId = _.first(
        _.get(selectedPrompt, "children.0.templateAssessmentIds")
      );

      return {
        fetchPolicy: "cache-and-network",
        context: { useHttpLink: true },
        variables: {
          id: assessmentId,
          uids: ["title", "description", "aiRole", "aiRules", "openingMessage"],
          portalType: "RESOURCE_LIBRARY",
        },
      };
    },
    props({
      getAITutorLibraryAssessmentDetails,
      ownProps: {
        isData,
        isLoading,
        selectedPrompt,
        selectedTab,
        aiConversationId,
      },
    }) {
      const { node, networkStatus } = getAITutorLibraryAssessmentDetails || {};
      const { fields, aiConversation } = node || {};

      const updatedSelectedPrompt = _.merge({}, selectedPrompt, {
        fields,
        aiConversation,
      });

      let _aiConversationId = null;

      if (isPlayMode(selectedTab)) {
        _aiConversationId = aiConversationId;
      } else {
        _aiConversationId = getConversationIdByEntitySubType({
          assessmentDetails: node,
          entitySubType: selectedTab,
        });
      }

      const aiAnalysisCriteria = getAIAnalysisItem(node);
      const isLibraryAssessmentLoading =
        _.includes([1, 2], networkStatus) || isLoading;

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: isLibraryAssessmentLoading,
        isLibraryAssessmentLoading,
        aiAnalysisCriteria,
        selectedPrompt: updatedSelectedPrompt,
        aiConversationId: _aiConversationId,
      };
    },
  }),
  getAITutorConversationsEnhancer
)(AITutorChatWrapper);

AITutorChatWrapper.defaultProps = {
  mode: "edit",
  selectedPrompt: {},
  emptyStateProps: {},
  course: {},
  entityId: "",
  gradeId: "",
  showEmptyState: false,
  conversationEntityType: "ASSESSMENT",
  showTabs: true,
  leId: "",
  entitySubType: "",
  source: "",
  showHeader: true,
  shouldGeneratePreviewChat: false,
  showCompactUI: false,
  onClose: null,
  showChatContainerFullWidth: true,
};

AITutorChatWrapper.propTypes = {
  selectedPrompt: PropTypes.object,
  emptyStateProps: PropTypes.object,
  course: PropTypes.object,
  entityId: PropTypes.string,
  gradeId: PropTypes.string,
  showEmptyState: PropTypes.bool,
  conversationEntityType: PropTypes.string,
  showTabs: PropTypes.bool,
  leId: PropTypes.string,
  entitySubType: PropTypes.string,
  source: PropTypes.string,
  showHeader: PropTypes.bool,
  shouldGeneratePreviewChat: PropTypes.bool,
  showCompactUI: PropTypes.bool,
  onClose: PropTypes.func,
  showChatContainerFullWidth: PropTypes.bool,
};
